import type { UseUserCreate } from '@openx/components-administration/lib/utils/context/UserHookTypes';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { PageNotFound, type PageNotFoundProps } from '@openx/components-core/lib/PageNotFound/PageNotFound';
import { HeaderActions } from '@openx/components-header/HeaderActions/HeaderActions';
import { PageHeader } from '@openx/components-header/PageHeader';
import type { OrganizationAllowedProvider } from '@openx/types/organization';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationContext } from '../../utils/context/OrganizationContext';
import { ApiKeysSection } from './sections/ApiKeysSection';
import { DetailsSection } from './sections/DetailsSection';
import { LinkedProvidersSection } from './sections/LinkedProvidersSection';
import { OpenAudienceAccessSection } from './sections/OpenAudienceAccessSection';
import { PixelSection } from './sections/PixelSection';
import { UsersWithAccess } from './sections/UsersWithAccess';

type OrganizationDetailsProps = {
  handleDeleteUser: ({ id, name }: { id: string; name: string }) => void;
  handleGoBack: () => void;
  handleCreateUser: ReturnType<UseUserCreate>['createUser'];
  organizationId: string;
  titlePrefix: PageNotFoundProps['appName'];
};

export const OrganizationDetails = ({
  handleGoBack,
  organizationId,
  titlePrefix,
  handleDeleteUser,
  handleCreateUser,
}: OrganizationDetailsProps) => {
  const { t } = useTranslation();
  const { useOrganizationFetch, useOrganizationsListFetch, useOrganizationPageHeaderActions } =
    useOrganizationContext();
  const { organization, loading } = useOrganizationFetch(organizationId);
  const actions = useOrganizationPageHeaderActions(organizationId);

  const { organizations: parentOrganizationsData, loading: parentOrganizationsLoading } = useOrganizationsListFetch({
    variables: { where: { id: { _in: organization?.path_to_root || [] } } },
    skip: !organization?.path_to_root || organization.path_to_root.length === 0,
  });

  const linkedProviders: OrganizationAllowedProvider[] | undefined = useMemo(
    () =>
      organization?.allowed_providers?.map(({ provider }) => ({
        ...provider,
        oa_match: provider.allowed_export_types.includes('oa_match'),
        bidstream: provider.allowed_export_types.includes('bidstream'),
        bidstream_plus: provider.allowed_export_types.includes('bidstream_plus'),
      })),
    [organization]
  );

  if (loading) return <Loader />;

  if (!organization || !organizationId) {
    return <PageNotFound appName={'OpenXSelect'} />;
  }

  return (
    <PageHeader
      goBack={handleGoBack}
      render={() => <HeaderActions actions={actions} />}
      title={t('Details: {name}', { name: organization.name })}
      titlePrefix={titlePrefix}
    >
      <DetailsSection
        organization={organization}
        parentOrganizationsData={parentOrganizationsData}
        parentOrganizationsLoading={parentOrganizationsLoading}
      />
      <UsersWithAccess
        handleCreateUser={handleCreateUser}
        handleDeleteUser={handleDeleteUser}
        organization={organization}
      />
      <OpenAudienceAccessSection organization={organization} />
      <PixelSection organization={organization} />
      <ApiKeysSection organizationId={organizationId} />
      <LinkedProvidersSection linkedProviders={linkedProviders} />
    </PageHeader>
  );
};
